import { API_V1 } from 'API/APIs/APIv1';
import { getFunnelById } from 'API/linkedin-funnel';
import {
  BlacklistScopes,
  Blacklist_New,
  BlacklistItem,
  BlacklistTypes,
} from 'features/co-pilot/company-blacklists/company-blacklist.types';
import _ from 'lodash';
import Logger from 'logger';

interface ModernBlacklist {
  _id: string;
  name: string;
  type: BlacklistTypes;
  isDefault?: boolean;
}

interface ModernBlacklistItem {
  _id?: string;
  contactLinkedinUrl?: string;
  contactVanityName?: string;
  companyName?: string;
  companyUrn?: string;
  companyLinkedinUrl?: string;
}

const normalizeBlacklistItem = (item: ModernBlacklistItem): BlacklistItem => ({
  _id: item._id,
  contactUrl: item.contactLinkedinUrl,
  contactLinkedinUrl: item.contactLinkedinUrl,
  contactVanityName: item.contactVanityName,
  companyName: item.companyName,
  companyUrn: item.companyUrn,
  companyLinkedinUrl: item.companyLinkedinUrl,
});

const toLegacyBlacklist = (
  blacklist: ModernBlacklist,
  items: ModernBlacklistItem[],
  scope: BlacklistScopes = BlacklistScopes.GLOBAL,
  associatedFunnel?: string
): Blacklist_New => ({
  _id: blacklist._id,
  name: blacklist.name,
  default: !!blacklist.isDefault,
  blacklistType: blacklist.type,
  blacklistScope: scope,
  associatedFunnel,
  items: items.map(normalizeBlacklistItem),
});

const toModernContactItems = (items: BlacklistItem[]) =>
  items.map((item) => ({
    contactLinkedinUrl: item.contactUrl || item.contactLinkedinUrl,
    contactVanityName: item.contactVanityName,
  }));

const toModernCompanyItems = (items: BlacklistItem[]) =>
  Array.from(
    new Map(
      items
        .map((item) => ({
          companyName: item.companyName?.trim(),
          companyUrn: item.companyUrn?.trim(),
          companyLinkedinUrl: item.companyLinkedinUrl?.trim(),
        }))
        .filter((item) => !!item.companyName)
        .map((item) => {
          const normalizedCompanyName = item.companyName!.toLowerCase();
          const normalizedCompanyUrn = item.companyUrn?.toLowerCase();
          const dedupeKey = normalizedCompanyUrn ? `urn:${normalizedCompanyUrn}` : `name:${normalizedCompanyName}`;
          return [dedupeKey, item] as const;
        })
    ).values()
  );

const getBlacklistItems = async (blacklistId: string): Promise<ModernBlacklistItem[]> => {
  try {
    const pageSize = 100;
    let offset = 0;
    let hasMore = true;
    const allItems: ModernBlacklistItem[] = [];

    while (hasMore) {
      const response = await API_V1.get(`/blacklists/${blacklistId}/items`, {
        params: {
          limit: pageSize,
          offset,
        },
      });

      const items = (response.data?.items || []) as ModernBlacklistItem[];
      allItems.push(...items);

      if (items.length < pageSize) {
        hasMore = false;
      } else {
        offset += pageSize;
      }
    }

    return allItems;
  } catch (e) {
    Logger.trace('Error while fetching blacklist items for %s', blacklistId);
    Logger.error(e);
    return [];
  }
};

const addItems = async (blacklistId: string, type: BlacklistTypes, items: BlacklistItem[]) => {
  if (_.isEmpty(items)) {
    return;
  }

  if (type === BlacklistTypes.CONTACT) {
    await API_V1.post(`/blacklists/${blacklistId}/contacts`, {
      items: toModernContactItems(items),
    });
    return;
  }

  const companyItems = toModernCompanyItems(items);
  if (_.isEmpty(companyItems)) {
    return;
  }

  await API_V1.post(`/blacklists/${blacklistId}/companies`, {
    items: companyItems,
  });
};

const getBlacklistByIdInternal = async (blacklistId: string): Promise<ModernBlacklist> => {
  const response = await API_V1.get(`/blacklists/${blacklistId}`);
  return response.data;
};

const resolveItemIdsForDelete = async (blacklistId: string, blacklist: ModernBlacklist, items: BlacklistItem[]): Promise<string[]> => {
  const directIds = items.map((item) => item._id).filter((id): id is string => !!id);
  if (!_.isEmpty(directIds)) {
    return directIds;
  }

  const existingItems = await getBlacklistItems(blacklistId);
  if (blacklist.type === BlacklistTypes.CONTACT) {
    const identifiers = new Set(
      items.map((item) => item.contactUrl || item.contactLinkedinUrl || item.contactVanityName).filter((value): value is string => !!value)
    );
    return existingItems
      .filter((item) => {
        const identifier = item.contactLinkedinUrl || item.contactVanityName;
        return !!identifier && identifiers.has(identifier);
      })
      .map((item) => item._id!)
      .filter(Boolean);
  }

  const identifiers = new Set(items.map((item) => item.companyName).filter((value): value is string => !!value));
  return existingItems
    .filter((item) => !!item.companyName && identifiers.has(item.companyName))
    .map((item) => item._id!)
    .filter(Boolean);
};

const removeItems = async (blacklistId: string, blacklist: ModernBlacklist, items: BlacklistItem[]) => {
  const itemIds = await resolveItemIdsForDelete(blacklistId, blacklist, items);
  if (_.isEmpty(itemIds)) {
    return;
  }
  await API_V1.delete(`/blacklists/${blacklistId}/items`, {
    data: {
      itemIds,
    },
  });
};

export const createBlacklist = async (name: string, isDefault: boolean): Promise<any> => {
  try {
    const response = await API_V1.post('/blacklists', {
      name,
      type: BlacklistTypes.COMPANY,
      isDefault,
    });

    const created = response.data as ModernBlacklist;
    const items = await getBlacklistItems(created._id);
    return toLegacyBlacklist(created, items, BlacklistScopes.GLOBAL);
  } catch (e) {
    Logger.trace('Error while creating a company blacklist!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const createGlobalBlacklist = async (blacklistType: BlacklistTypes, name: string, items: BlacklistItem[]): Promise<any> => {
  try {
    const response = await API_V1.post('/blacklists', {
      name,
      type: blacklistType,
      isDefault: false,
    });

    const created = response.data as ModernBlacklist;
    await addItems(created._id, blacklistType, items);
    const refreshedItems = await getBlacklistItems(created._id);
    return toLegacyBlacklist(created, refreshedItems, BlacklistScopes.GLOBAL);
  } catch (e) {
    Logger.trace('Error while creating a company blacklist!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const createLocalBlacklist = async (
  blacklistType: BlacklistTypes,
  name: string,
  items: BlacklistItem[],
  associatedFunnel: string
): Promise<any> => {
  try {
    const response = await API_V1.post('/blacklists', {
      name,
      type: blacklistType,
      isDefault: false,
    });

    const created = response.data as ModernBlacklist;
    await addItems(created._id, blacklistType, items);
    const refreshedItems = await getBlacklistItems(created._id);
    return toLegacyBlacklist(created, refreshedItems, BlacklistScopes.LOCAL, associatedFunnel);
  } catch (e) {
    Logger.trace('Error while creating a company blacklist!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getAllBlacklists = async (funnelId?: string): Promise<any> => {
  try {
    const response = await API_V1.get('/blacklists', {
      params: { funnelId },
    });
    let modernBlacklists: ModernBlacklist[] = response.data?.blacklists || [];

    if (funnelId) {
      const funnel = await getFunnelById(funnelId);
      if (!funnel?.error) {
        const selected = new Set((funnel?.blacklists || []) as string[]);
        modernBlacklists = modernBlacklists.filter((blacklist) => selected.has(blacklist._id));
      }
    }

    const withItems = await Promise.all(
      modernBlacklists.map(async (blacklist) => {
        const items = await getBlacklistItems(blacklist._id);
        return toLegacyBlacklist(blacklist, items, BlacklistScopes.GLOBAL, funnelId);
      })
    );

    return { response: withItems };
  } catch (e) {
    Logger.trace('Error while getting all company blacklists!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getBlacklistById = async (blacklistId: string): Promise<any> => {
  try {
    const blacklist = await getBlacklistByIdInternal(blacklistId);
    const items = await getBlacklistItems(blacklistId);
    return toLegacyBlacklist(blacklist, items, BlacklistScopes.GLOBAL);
  } catch (e) {
    Logger.trace('Error while getting the company blacklist %s!', blacklistId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateBlacklist = async (blacklistId: string, blacklistData: Partial<Blacklist_New>): Promise<any> => {
  try {
    await API_V1.patch(`/blacklists/${blacklistId}`, blacklistData);
    const blacklist = await getBlacklistByIdInternal(blacklistId);
    const items = await getBlacklistItems(blacklistId);
    return toLegacyBlacklist(blacklist, items, BlacklistScopes.GLOBAL);
  } catch (e) {
    Logger.trace('Error while updating blacklist %s!', blacklistId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateBlacklistNew = async (blacklistId: string, blacklistData: Partial<Blacklist_New>): Promise<any> => {
  try {
    const blacklist = await getBlacklistByIdInternal(blacklistId);

    if (!_.isNil(blacklistData.name)) {
      await API_V1.patch(`/blacklists/${blacklistId}`, {
        name: blacklistData.name,
      });
    }

    if (!_.isEmpty(blacklistData.items)) {
      const action = blacklistData.action || 'add';

      if (action === 'add') {
        await addItems(blacklistId, blacklist.type, blacklistData.items || []);
      } else if (action === 'remove') {
        await removeItems(blacklistId, blacklist, blacklistData.items || []);
      } else if (action === 'replace') {
        const currentItems = await getBlacklistItems(blacklistId);
        const allItemIds = currentItems.map((item) => item._id).filter((id): id is string => !!id);
        if (!_.isEmpty(allItemIds)) {
          await API_V1.delete(`/blacklists/${blacklistId}/items`, {
            data: {
              itemIds: allItemIds,
            },
          });
        }
        await addItems(blacklistId, blacklist.type, blacklistData.items || []);
      }
    }

    const updatedBlacklist = await getBlacklistByIdInternal(blacklistId);
    const updatedItems = await getBlacklistItems(blacklistId);
    return toLegacyBlacklist(updatedBlacklist, updatedItems, BlacklistScopes.GLOBAL);
  } catch (e) {
    Logger.trace('Error while updating blacklist %s!', blacklistId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const deleteBlacklist = async (blacklistId: string): Promise<any> => {
  try {
    const response = await API_V1.delete(`/blacklists/${blacklistId}`);
    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting blacklist %s!', blacklistId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};
